import { useState } from 'react';
import { backendCloudReportApi } from './cloudReportApi';
import {
  CODE_LABELS,
  RISK_OPTIONS,
  sectionCodeNames,
  sectionCodes,
} from './cloudReportModel';
import { buildReportDocx, docxFileName, fetchDocxTemplate } from './reportDocx';
import { downloadBlob } from './reportPdf';
import { lookupProse } from './reportTemplates';
import useCloudReport from './useCloudReport';

const inputClass = 'block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function formatTimestamp(value) {
  if (!value) return null;
  const date = value.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
}

function formatNumber(value) {
  const number = Number(value);
  return value === null || value === undefined || !Number.isFinite(number) ? '—' : number.toFixed(1);
}

function CodeSelect({ heading, name, value, onChange }) {
  const labels = CODE_LABELS[name];
  const needsChoice = value === null || value === undefined;
  const label = name.toUpperCase();
  return (
    <label className="block text-sm">
      <span className="mb-1 flex items-center gap-2 font-medium text-gray-700">
        {label}
        {needsChoice && (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800">Needs choice</span>
        )}
      </span>
      <select
        aria-label={`${heading} ${label}`}
        className={classNames(
          'w-full rounded-md border py-1.5 pl-2 pr-8 text-sm shadow-sm focus:outline-none focus:ring-1',
          needsChoice
            ? 'border-amber-400 bg-amber-50 focus:border-amber-500 focus:ring-amber-500'
            : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
        )}
        onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}
        value={needsChoice ? '' : String(value)}
      >
        <option value="">Choose…</option>
        {Object.entries(labels).map(([id, text]) => (
          <option key={id} value={id}>{`${text} (${id})`}</option>
        ))}
      </select>
    </label>
  );
}

function Evidence({ cells }) {
  if (!cells?.length) return null;
  return (
    <ul className="space-y-0.5 text-xs text-gray-500" aria-label="Evidence">
      {cells.map((cell) => (
        <li key={cell.tanner}>
          <span className="font-medium text-gray-600">{cell.tanner}</span>
          {`: image ${formatNumber(cell.image)} (normal ${formatNumber(cell.imageLower)}–${formatNumber(cell.imageUpper)}, DA ${cell.da ?? '—'})`}
          {` · sexual ${formatNumber(cell.sexual)} (normal up to ${formatNumber(cell.sexualUpper)}${cell.aboveNormalSexual ? ', above' : ''})`}
          {` · face ${formatNumber(cell.face)}`}
        </li>
      ))}
    </ul>
  );
}

function SectionBlock({ section, codes, templateKeys, onCode, onText, onRevert }) {
  const scored = sectionCodes(codes, section.id);
  const templateProse = section.key ? lookupProse(templateKeys, section.key) : '';
  const names = sectionCodeNames(section.id);
  return (
    <section className="rounded-lg border border-gray-200 p-4" aria-label={section.heading}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="text-base font-semibold text-gray-900">{section.heading}</h4>
        <span className="text-xs text-gray-500">{section.key ?? 'No key yet'}</span>
      </div>
      <div className={classNames('mt-3 grid gap-3', names.length > 3 ? 'sm:grid-cols-4' : 'sm:grid-cols-3')}>
        {names.map((name) => (
          <CodeSelect
            heading={section.heading}
            key={name}
            name={name}
            onChange={(value) => onCode(section.id, name, value)}
            value={scored[name]}
          />
        ))}
      </div>
      {section.id !== 'caution' && (
        <div className="mt-2">
          <Evidence cells={scored.evidence?.cells} />
        </div>
      )}
      <div className="mt-3 space-y-1">
        {!section.key && section.needs?.length > 0 && (
          <p className="text-sm text-amber-800">{`Choose ${section.needs.join(', ')} to pick a template paragraph.`}</p>
        )}
        {section.key && !templateProse && templateKeys && (
          <p className="text-sm text-amber-800">{`No template paragraph for ${section.key}`}</p>
        )}
        {section.state === 'edited' && (
          <p className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            Edited for this report only. Code changes will not replace this text.
            {templateProse && (
              <button
                className="font-medium text-indigo-600 hover:text-indigo-800"
                onClick={() => onRevert(section.id)}
                type="button"
              >
                Use template paragraph
              </button>
            )}
          </p>
        )}
      </div>
      <label className="mt-2 block text-sm">
        <span className="sr-only">{`${section.heading} paragraph`}</span>
        <textarea
          aria-label={`${section.heading} paragraph`}
          className={inputClass}
          onChange={(event) => onText(section.id, event.target.value)}
          rows={4}
          value={section.text}
        />
      </label>
    </section>
  );
}

export function ReportNotGenerated() {
  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
      <h3 className="text-base font-semibold text-gray-900">Report not generated yet</h3>
      <p className="mt-1 text-sm text-gray-600">
        Reports are generated automatically when an evaluation is uploaded. There is no report for this
        evaluation yet, so there is nothing to edit. Ask an operator to generate it.
      </p>
    </div>
  );
}

export function ReportStatusNotice({ status }) {
  if (status === 'loading') {
    return <p className="py-4 text-sm text-gray-500" role="status">Loading report…</p>;
  }
  if (status === 'error') {
    return <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">Could not load this report. Reload the page to try again.</div>;
  }
  if (status === 'missing') return <ReportNotGenerated />;
  return null;
}

/**
 * Editor for the report vasi_functions generated on upload
 * (cloud_eval_reports/{evaluationId}). It never scores: codes come from the
 * generated report or the clinician, and changing a code only looks up that
 * section's paragraph in the published template. The state comes from
 * useCloudReport, which the page shares with the scoring form, so both always
 * show the same codes and text; saving PATCHes just the changed fields
 * through vasi-backend.
 */
export function CloudReportEditor({
  reportState,
  loadDocxTemplate = fetchDocxTemplate,
  download = downloadBlob,
}) {
  const {
    status, report, draft, templateKeys, dirty, saving, message, setMessage,
    update, setCode, setSectionText, revertSection, setSummaryText, save,
  } = reportState;
  const [exporting, setExporting] = useState(false);

  if (status !== 'ready') return <ReportStatusNotice status={status} />;

  const sex = report?.sex ?? null;
  const templateError = !templateKeys
    ? 'There is no published template, so code changes will not swap paragraphs.'
    : null;
  const needsCount = draft.sections.filter((section) => !section.key).length;
  const missingCount = draft.sections.filter((section) => section.key && !section.text.trim()).length;
  const editedAt = formatTimestamp(report.editedAt);

  const setHeader = (name, value) => update((current) => ({
    ...current,
    header: { ...current.header, [name]: value },
  }));

  async function handleDownload() {
    setExporting(true);
    setMessage(null);
    try {
      const template = await loadDocxTemplate();
      const blob = await buildReportDocx(template, draft, { sex });
      download(blob, docxFileName({ caseNumber: report.caseNumber, clientName: draft.header.clientName }));
    } catch (error) {
      console.error('Failed to build the Word report:', error);
      setMessage({ tone: 'error', text: error.message || 'Could not build the Word report.' });
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-10 -mx-2 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-white/95 px-2 py-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Report</h3>
          <p className="text-sm text-gray-600">
            {report.status === 'edited' ? `Edited${editedAt ? ` ${editedAt}` : ''}` : 'Generated, not yet edited'}
            {report.template?.version != null ? ` · template v${report.template.version}` : ''}
            {dirty && <span className="ml-2 font-medium text-amber-700">Unsaved changes</span>}
          </p>
          {(needsCount > 0 || missingCount > 0) && (
            <p className="text-sm text-amber-800">
              {[
                needsCount > 0 ? `${needsCount} ${needsCount === 1 ? 'section needs' : 'sections need'} codes` : null,
                missingCount > 0 ? `${missingCount} ${missingCount === 1 ? 'section has' : 'sections have'} no paragraph` : null,
              ].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
            disabled={exporting}
            onClick={handleDownload}
            type="button"
          >
            {exporting ? 'Building…' : 'Download Word report'}
          </button>
          <button
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-indigo-300"
            disabled={saving || !dirty}
            onClick={save}
            type="button"
          >
            {saving ? 'Saving…' : 'Save report'}
          </button>
        </div>
      </div>

      {message && (
        <p className={classNames('text-sm', message.tone === 'error' ? 'text-red-700' : 'text-green-700')} role="status">
          {message.text}
        </p>
      )}
      {templateError && <p className="text-sm text-amber-800">{templateError}</p>}

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" aria-label="Report header">
        {[
          ['clientName', 'Client name'],
          ['honorific', 'Honorific'],
        ].map(([name, label]) => (
          <label className="block text-sm" key={name}>
            <span className="mb-1 block font-medium text-gray-700">{label}</span>
            <input className={inputClass} onChange={(event) => setHeader(name, event.target.value)} value={draft.header[name]} />
          </label>
        ))}
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-gray-700">Age</span>
          <input
            className={inputClass}
            min="0"
            onChange={(event) => setHeader('age', event.target.value === '' ? null : Number(event.target.value))}
            type="number"
            value={draft.header.age ?? ''}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-gray-700">Assessment date</span>
          <input
            className={inputClass}
            onChange={(event) => setHeader('assessmentDate', event.target.value)}
            type="date"
            value={draft.header.assessmentDate}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-gray-700">Provider</span>
          <input className={inputClass} onChange={(event) => setHeader('provider', event.target.value)} value={draft.header.provider} />
        </label>
      </section>

      <label className="block text-sm">
        <span className="mb-1 block font-medium text-gray-700">Introduction</span>
        <textarea
          className={inputClass}
          onChange={(event) => update((current) => ({ ...current, intro: event.target.value }))}
          rows={3}
          value={draft.intro}
        />
      </label>

      <div className="space-y-4">
        {draft.sections.map((section) => (
          <SectionBlock
            codes={draft.codes}
            key={section.id}
            onCode={(sectionId, name, value) => setCode(sectionId, name, value)}
            onRevert={(sectionId) => revertSection(sectionId)}
            onText={(sectionId, text) => setSectionText(sectionId, text)}
            section={section}
            templateKeys={templateKeys}
          />
        ))}
      </div>

      <label className="block text-sm">
        <span className="mb-1 block font-medium text-gray-700">Summary</span>
        <textarea
          className={inputClass}
          onChange={(event) => setSummaryText(event.target.value)}
          rows={4}
          value={draft.summary.text}
        />
      </label>

      <label className="block max-w-xs text-sm">
        <span className="mb-1 block font-medium text-gray-700">Risk of recidivism</span>
        <select
          className={inputClass}
          onChange={(event) => update((current) => ({ ...current, risk: event.target.value || null }))}
          value={draft.risk ?? ''}
        >
          <option value="">Not set</option>
          {RISK_OPTIONS.map((risk) => <option key={risk} value={risk}>{risk}</option>)}
        </select>
      </label>

      <label className="block text-sm">
        <span className="mb-1 block font-medium text-gray-700">Closing</span>
        <textarea
          className={inputClass}
          onChange={(event) => update((current) => ({ ...current, closing: event.target.value }))}
          rows={4}
          value={draft.closing}
        />
        <span className="mt-1 block text-xs text-gray-500">
          The risk choice fills the [] in &quot;suggest a [] risk of recidivism&quot;.
        </span>
      </label>
    </div>
  );
}

/** Standalone editor that loads its own copy of the report. */
export default function CloudEvalReport({ evaluation, api = backendCloudReportApi, ...props }) {
  const reportState = useCloudReport(evaluation?.id, api);
  return <CloudReportEditor reportState={reportState} {...props} />;
}
